import React, { useState, useEffect } from "react";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import FileCopyOutlined from "@material-ui/icons/FileCopyOutlined";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import Add from "@material-ui/icons/Add";
import Button from "@material-ui/core/Button";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		alignItems: "center",
		padding: theme.spacing(8, 8, 3),
	},

	paper: {
		padding: theme.spacing(2),
		display: "flex",
		alignItems: "center",
		marginBottom: 12,

	},

	settingOption: {
		marginLeft: "auto",
	},
	margin: {
		margin: theme.spacing(1),
	},
	sectionTitle: {
		margin: theme.spacing(3, 0, 1),
	},
	fieldPaper: {
		padding: theme.spacing(2),
		marginBottom: 12,
	},

}));

const defaultSupportFlow = {
	menu: {
		intro: "Hola, soy el asistente de GNS.",
		supportLabel: "Solicitar soporte tecnico",
		statusLabel: "Consultar estado de una solicitud",
		advisorLabel: "Hablar con un asesor",
	},
	categories: [
		{ label: "Conexion a internet", queueId: 0 },
		{ label: "Equipos y puestos de trabajo", queueId: 0 },
		{ label: "Correo y colaboracion", queueId: 0 },
		{ label: "Sistemas y aplicaciones", queueId: 0 },
		{ label: "Camaras y CCTV", queueId: 0 },
		{ label: "Otro", queueId: 0 },
	],
	impacts: [
		{ label: "Bajo", queueId: 0 },
		{ label: "Normal", queueId: 0 },
		{ label: "Alto", queueId: 0 },
		{ label: "Critico", queueId: 0 },
	],
	fields: {
		customerTypeEnabled: true,
		customerTypePrompt: "Para orientar la atencion, indica la relacion con GNS:\n\n*1* - Cliente con contrato\n*2* - Cliente ocasional\n*3* - No estoy seguro",
		companyEnabled: true,
		companyPrompt: "Indica el nombre de tu empresa o escribe *OMITIR*.",
		descriptionPrompt: "Describe el problema con el mayor detalle posible.",
		scheduleEnabled: true,
		schedulePrompt: "Indica el horario preferido para contactarte o escribe *AHORA*.",
	},
	messages: {
		advisor: "Recibimos tu solicitud. Un asesor continuara la atencion por este medio.",
		submitted: "Registramos tu solicitud. Un asesor validara los datos y te informara el numero de ticket de soporte.",
	},
	routing: { contractQueueId: 0, occasionalQueueId: 0 },
};

const parseSupportFlow = value => {
	try {
		const parsed = JSON.parse(value || "{}");
		return {
			...defaultSupportFlow,
			...parsed,
			menu: { ...defaultSupportFlow.menu, ...(parsed.menu || {}) },
			fields: { ...defaultSupportFlow.fields, ...(parsed.fields || {}) },
			messages: { ...defaultSupportFlow.messages, ...(parsed.messages || {}) },
			routing: { ...defaultSupportFlow.routing, ...(parsed.routing || {}) },
			categories: Array.isArray(parsed.categories) && parsed.categories.length ? parsed.categories : defaultSupportFlow.categories,
			impacts: Array.isArray(parsed.impacts) && parsed.impacts.length ? parsed.impacts : defaultSupportFlow.impacts,
		};
	} catch {
		return defaultSupportFlow;
	}
};

const Settings = () => {
	const classes = useStyles();

	const [settings, setSettings] = useState([]);
	const [showToken, setShowToken] = useState(false);
	const [botRules, setBotRules] = useState([]);
	const [queues, setQueues] = useState([]);
	const [supportFlow, setSupportFlow] = useState(defaultSupportFlow);

	useEffect(() => {
		const fetchSession = async () => {
			try {
				const { data } = await api.get("/settings");
				setSettings(data);
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, []);

	useEffect(() => {
		const fetchQueues = async () => {
			try {
				const { data } = await api.get("/queue");
				setQueues(data);
			} catch (err) {
				toastError(err);
			}
		};
		fetchQueues();
	}, []);

	useEffect(() => {
		try {
			setBotRules(JSON.parse(getSettingValue("botRules") || "[]"));
		} catch {
			setBotRules([]);
		}
	}, [settings]);

	useEffect(() => {
		setSupportFlow(parseSupportFlow(getSettingValue("supportIntakeFlow")));
	}, [settings]);

	useEffect(() => {
		const socket = openSocket();

		socket.on("settings", data => {
			if (data.action === "update") {
				setSettings(prevState => {
					const aux = [...prevState];
					const settingIndex = aux.findIndex(s => s.key === data.setting.key);
					if (settingIndex >= 0) {
						aux[settingIndex].value = data.setting.value;
					} else {
						aux.push(data.setting);
					}
					return aux;
				});
			}
		});

		return () => {
			socket.disconnect();
		};
	}, []);

	const updateLocalSetting = (settingKey, selectedValue) => {
		setSettings(prevState => {
			const aux = [...prevState];
			const settingIndex = aux.findIndex(s => s.key === settingKey);

			if (settingIndex >= 0) {
				aux[settingIndex] = {
					...aux[settingIndex],
					value: selectedValue,
				};
			}

			return aux;
		});
	};

	const handleChangeSetting = async e => {
		const selectedValue = e.target.value;
		const settingKey = e.target.name;

		try {
			await api.put(`/settings/${settingKey}`, {
				value: selectedValue,
			});
			updateLocalSetting(settingKey, selectedValue);
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const handleLocalChangeSetting = e => {
		updateLocalSetting(e.target.name, e.target.value);
	};

	const getSettingValue = key => {
		return settings.find(s => s.key === key)?.value || "";
	};

	const updateSupportFlow = updater => {
		setSupportFlow(current => updater(JSON.parse(JSON.stringify(current))));
	};

	const updateSupportFlowValue = (section, key, value) => {
		updateSupportFlow(current => {
			current[section][key] = value;
			return current;
		});
	};

	const updateSupportChoice = (section, index, key, value) => {
		updateSupportFlow(current => {
			current[section][index][key] = value;
			return current;
		});
	};

	const saveSupportFlow = async () => {
		try {
			const value = JSON.stringify(supportFlow);
			await api.put("/settings/supportIntakeFlow", { value });
			updateLocalSetting("supportIntakeFlow", value);
			toast.success("Flujo de soporte actualizado");
		} catch (err) {
			toastError(err);
		}
	};

	const handleCopyToken = () => {
		const token = getSettingValue("userApiToken");
		if (!token) return;
		navigator.clipboard.writeText(token);
		toast.success(i18n.t("settings.dashboard.tokenCopied"));
	};

	const saveBotRules = async nextRules => {
		try {
			await api.put("/settings/botRules", { value: JSON.stringify(nextRules) });
			setBotRules(nextRules);
			updateLocalSetting("botRules", JSON.stringify(nextRules));
			toast.success("Reglas del bot guardadas.");
		} catch (err) {
			toastError(err);
		}
	};

	const updateBotRule = (index, key, value) => {
		const nextRules = botRules.map((rule, ruleIndex) =>
			ruleIndex === index ? { ...rule, [key]: value } : rule
		);
		setBotRules(nextRules);
	};

	const renderEnabledSelect = key => (
		<Select
			margin="dense"
			variant="outlined"
			native
			id={`${key}-setting`}
			name={key}
			value={getSettingValue(key)}
			className={classes.settingOption}
			onChange={handleChangeSetting}
		>
			<option value="enabled">
				{i18n.t("settings.options.enabled")}
			</option>
			<option value="disabled">
				{i18n.t("settings.options.disabled")}
			</option>
		</Select>
	);

	const renderTextSetting = (key, label, props = {}) => (
		<Paper className={classes.fieldPaper}>
			<TextField
				id={`${key}-setting`}
				label={label}
				margin="dense"
				variant="outlined"
				fullWidth
				name={key}
				value={getSettingValue(key)}
				onChange={handleLocalChangeSetting}
				onBlur={handleChangeSetting}
				{...props}
			/>
		</Paper>
	);

	return (
		<div className={classes.root}>
			<Container className={classes.container} maxWidth="md">
				<Paper className={classes.paper}>
					<Typography variant="body1">
						{i18n.t("settings.settings.userCreation.name")}
					</Typography>
					{renderEnabledSelect("userCreation")}

				</Paper>

				<Typography className={classes.sectionTitle} variant="body2" gutterBottom>
					Seguridad de sesiones
				</Typography>
				{renderTextSetting(
					"authAccessTokenMinutes",
					"Sesión activa (minutos)",
					{ type: "number", inputProps: { min: 5, max: 1440, step: 1 } }
				)}
				{renderTextSetting(
					"authRefreshTokenDays",
					"Renovación de sesión (días)",
					{ type: "number", inputProps: { min: 1, max: 30, step: 1 } }
				)}

				<Typography className={classes.sectionTitle} variant="body2" gutterBottom>
					{i18n.t("settings.automations.title")}
				</Typography>

				<Paper className={classes.paper}>
					<Typography variant="body1">
						{i18n.t("settings.automations.autoReplyEnabled")}
					</Typography>
					{renderEnabledSelect("autoReplyEnabled")}
				</Paper>

				{renderTextSetting(
					"autoReplyMessage",
					i18n.t("settings.automations.autoReplyMessage"),
					{ multiline: true, rows: 3 }
				)}

				<Paper className={classes.paper}>
					<Typography variant="body1">
						{i18n.t("settings.automations.businessHoursEnabled")}
					</Typography>
					{renderEnabledSelect("businessHoursEnabled")}
				</Paper>

				{renderTextSetting(
					"businessHoursTimezone",
					i18n.t("settings.automations.businessHoursTimezone")
				)}
				{renderTextSetting(
					"businessHoursDays",
					i18n.t("settings.automations.businessHoursDays")
				)}
				{renderTextSetting(
					"businessHoursStart",
					i18n.t("settings.automations.businessHoursStart")
				)}
				{renderTextSetting(
					"businessHoursEnd",
					i18n.t("settings.automations.businessHoursEnd")
				)}

				<Paper className={classes.paper}>
					<Typography variant="body1">
						{i18n.t("settings.automations.afterHoursReplyEnabled")}
					</Typography>
					{renderEnabledSelect("afterHoursReplyEnabled")}
				</Paper>

				{renderTextSetting(
					"afterHoursReplyMessage",
					i18n.t("settings.automations.afterHoursReplyMessage"),
					{ multiline: true, rows: 3 }
				)}

				<Paper className={classes.paper}>
					<Typography variant="body1">
						{i18n.t("settings.automations.queueMenuEnabled")}
					</Typography>
					{renderEnabledSelect("queueMenuEnabled")}
				</Paper>

				<Typography className={classes.sectionTitle} variant="body2" gutterBottom>
					Menu y formulario de soporte
				</Typography>
				<Paper className={classes.paper}>
					<Typography variant="body1">Activar menu por mensaje MENU</Typography>
					{renderEnabledSelect("supportIntakeEnabled")}
				</Paper>
				<Paper className={classes.fieldPaper}>
					<Typography variant="subtitle1" gutterBottom>Menu para el cliente</Typography>
					<TextField fullWidth label="Saludo" value={supportFlow.menu.intro} onChange={event => updateSupportFlowValue("menu", "intro", event.target.value)} />
					<TextField fullWidth label="Opcion 1" value={supportFlow.menu.supportLabel} onChange={event => updateSupportFlowValue("menu", "supportLabel", event.target.value)} style={{ marginTop: 12 }} />
					<TextField fullWidth label="Opcion 2" value={supportFlow.menu.statusLabel} onChange={event => updateSupportFlowValue("menu", "statusLabel", event.target.value)} style={{ marginTop: 12 }} />
					<TextField fullWidth label="Opcion 3" value={supportFlow.menu.advisorLabel} onChange={event => updateSupportFlowValue("menu", "advisorLabel", event.target.value)} style={{ marginTop: 12 }} />
				</Paper>
				<Paper className={classes.fieldPaper}>
					<Typography variant="subtitle1" gutterBottom>Categorias y derivacion</Typography>
					{supportFlow.categories.map((category, index) => (
						<div key={`category-${index}`} style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 10 }}>
							<TextField fullWidth label={`Categoria ${index + 1}`} value={category.label || ""} onChange={event => updateSupportChoice("categories", index, "label", event.target.value)} />
							<Select native variant="outlined" value={category.queueId || ""} onChange={event => updateSupportChoice("categories", index, "queueId", Number(event.target.value || 0))} style={{ minWidth: 180 }}>
								<option value="">Cola general</option>
								{queues.map(queue => <option key={queue.id} value={queue.id}>{queue.name}</option>)}
							</Select>
							<IconButton disabled={supportFlow.categories.length === 1} onClick={() => updateSupportFlow(current => ({ ...current, categories: current.categories.filter((_, itemIndex) => itemIndex !== index) }))}><DeleteOutline /></IconButton>
						</div>
					))}
					<Button size="small" startIcon={<Add />} onClick={() => updateSupportFlow(current => ({ ...current, categories: [...current.categories, { label: "Nueva categoria", queueId: 0 }] }))} style={{ marginTop: 12 }}>Agregar categoria</Button>
				</Paper>
				<Paper className={classes.fieldPaper}>
					<Typography variant="subtitle1" gutterBottom>Impacto</Typography>
					{supportFlow.impacts.map((impact, index) => (
						<div key={`impact-${index}`} style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 10 }}>
							<TextField fullWidth label={`Nivel ${index + 1}`} value={impact.label || ""} onChange={event => updateSupportChoice("impacts", index, "label", event.target.value)} />
							<IconButton disabled={supportFlow.impacts.length === 1} onClick={() => updateSupportFlow(current => ({ ...current, impacts: current.impacts.filter((_, itemIndex) => itemIndex !== index) }))}><DeleteOutline /></IconButton>
						</div>
					))}
					<Button size="small" startIcon={<Add />} onClick={() => updateSupportFlow(current => ({ ...current, impacts: [...current.impacts, { label: "Nuevo nivel", queueId: 0 }] }))} style={{ marginTop: 12 }}>Agregar nivel</Button>
				</Paper>
				<Paper className={classes.fieldPaper}>
					<Typography variant="subtitle1" gutterBottom>Preguntas y separacion comercial</Typography>
					<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
						<Typography variant="body2" style={{ flex: 1 }}>Preguntar si es cliente con contrato u ocasional</Typography>
						<Select native variant="outlined" value={supportFlow.fields.customerTypeEnabled ? "enabled" : "disabled"} onChange={event => updateSupportFlowValue("fields", "customerTypeEnabled", event.target.value === "enabled")}>
							<option value="enabled">Habilitado</option><option value="disabled">Deshabilitado</option>
						</Select>
					</div>
					{supportFlow.fields.customerTypeEnabled && <TextField fullWidth multiline rows={4} label="Pregunta de relacion comercial" value={supportFlow.fields.customerTypePrompt} onChange={event => updateSupportFlowValue("fields", "customerTypePrompt", event.target.value)} style={{ marginTop: 12 }} />}
					<div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
						<Typography variant="body2" style={{ flex: 1 }}>Preguntar empresa</Typography>
						<Select native variant="outlined" value={supportFlow.fields.companyEnabled ? "enabled" : "disabled"} onChange={event => updateSupportFlowValue("fields", "companyEnabled", event.target.value === "enabled")}>
							<option value="enabled">Habilitado</option><option value="disabled">Deshabilitado</option>
						</Select>
					</div>
					{supportFlow.fields.companyEnabled && <TextField fullWidth label="Pregunta de empresa" value={supportFlow.fields.companyPrompt} onChange={event => updateSupportFlowValue("fields", "companyPrompt", event.target.value)} style={{ marginTop: 12 }} />}
					<TextField fullWidth multiline rows={2} label="Pregunta de detalle" value={supportFlow.fields.descriptionPrompt} onChange={event => updateSupportFlowValue("fields", "descriptionPrompt", event.target.value)} style={{ marginTop: 12 }} />
					<div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
						<Typography variant="body2" style={{ flex: 1 }}>Preguntar horario de contacto</Typography>
						<Select native variant="outlined" value={supportFlow.fields.scheduleEnabled ? "enabled" : "disabled"} onChange={event => updateSupportFlowValue("fields", "scheduleEnabled", event.target.value === "enabled")}>
							<option value="enabled">Habilitado</option><option value="disabled">Deshabilitado</option>
						</Select>
					</div>
					{supportFlow.fields.scheduleEnabled && <TextField fullWidth label="Pregunta de horario" value={supportFlow.fields.schedulePrompt} onChange={event => updateSupportFlowValue("fields", "schedulePrompt", event.target.value)} style={{ marginTop: 12 }} />}
					<div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16 }}>
						<Typography variant="body2" style={{ flex: 1 }}>Cola para clientes con contrato registrado</Typography>
						<Select native variant="outlined" value={supportFlow.routing.contractQueueId || ""} onChange={event => updateSupportFlowValue("routing", "contractQueueId", Number(event.target.value || 0))}>
							<option value="">Usar cola general</option>{queues.map(queue => <option key={queue.id} value={queue.id}>{queue.name}</option>)}
						</Select>
					</div>
					<div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
						<Typography variant="body2" style={{ flex: 1 }}>Cola para clientes ocasionales</Typography>
						<Select native variant="outlined" value={supportFlow.routing.occasionalQueueId || ""} onChange={event => updateSupportFlowValue("routing", "occasionalQueueId", Number(event.target.value || 0))}>
							<option value="">Usar cola general</option>{queues.map(queue => <option key={queue.id} value={queue.id}>{queue.name}</option>)}
						</Select>
					</div>
				</Paper>
				<Paper className={classes.paper}>
					<Typography variant="body1">Derivar solicitudes confirmadas a cola</Typography>
					<Select
						margin="dense"
						variant="outlined"
						native
						name="supportIntakeQueueId"
						value={getSettingValue("supportIntakeQueueId")}
						className={classes.settingOption}
						onChange={handleChangeSetting}
					>
						<option value="">No derivar</option>
						{queues.map(queue => <option key={queue.id} value={queue.id}>{queue.name}</option>)}
					</Select>
				</Paper>
				<Paper className={classes.fieldPaper}>
					<Typography variant="subtitle1" gutterBottom>Mensajes finales</Typography>
					<TextField fullWidth multiline rows={3} label="Mensaje para hablar con asesor" value={supportFlow.messages.advisor} onChange={event => updateSupportFlowValue("messages", "advisor", event.target.value)} />
					<TextField fullWidth multiline rows={3} label="Mensaje al enviar solicitud" value={supportFlow.messages.submitted} onChange={event => updateSupportFlowValue("messages", "submitted", event.target.value)} style={{ marginTop: 12 }} />
					<Button variant="contained" color="primary" onClick={saveSupportFlow} style={{ marginTop: 16 }}>Guardar flujo de soporte</Button>
				</Paper>

				<Typography className={classes.sectionTitle} variant="body2" gutterBottom>
					Encuesta de satisfacción
				</Typography>
				<Paper className={classes.paper}>
					<Typography variant="body1">Enviar CSAT al cerrar ticket</Typography>
					{renderEnabledSelect("csatEnabled")}
				</Paper>
				{renderTextSetting("csatMessage", "Mensaje de encuesta", { multiline: true, rows: 3 })}
				{renderTextSetting("csatThankYouMessage", "Mensaje de agradecimiento", { multiline: true, rows: 2 })}
				{renderTextSetting("csatExpiryHours", "Vigencia de encuesta (horas)", { type: "number", inputProps: { min: 1, max: 168, step: 1 } })}

				<Typography className={classes.sectionTitle} variant="body2" gutterBottom>
					Cierre por inactividad
				</Typography>
				<Paper className={classes.paper}>
					<Typography variant="body1">Cerrar tickets sin actividad</Typography>
					{renderEnabledSelect("autoCloseEnabled")}
				</Paper>
				{renderTextSetting("autoCloseHours", "Inactividad máxima (horas)", { type: "number", inputProps: { min: 1, max: 720, step: 1 } })}
				{renderTextSetting("autoCloseMessage", "Mensaje antes del cierre", { multiline: true, rows: 3 })}

				<Typography className={classes.sectionTitle} variant="body2" gutterBottom>
					Bot por reglas
				</Typography>
				<Paper className={classes.paper}>
					<Typography variant="body1">Activar bot por reglas</Typography>
					{renderEnabledSelect("botRulesEnabled")}
				</Paper>
				{botRules.map((rule, index) => (
					<Paper className={classes.fieldPaper} key={index}>
						<TextField
							label="Palabras clave (separadas por coma)"
							value={rule.keywords || ""}
							onChange={event => updateBotRule(index, "keywords", event.target.value)}
							fullWidth margin="dense" variant="outlined"
						/>
						<Paper className={classes.paper}>
							<Typography variant="body2">Tipo de coincidencia</Typography>
							<Select native value={rule.matchMode || "contains"} className={classes.settingOption}
								onChange={event => updateBotRule(index, "matchMode", event.target.value)}>
								<option value="contains">Contiene la palabra</option>
								<option value="exact">Mensaje exacto</option>
							</Select>
						</Paper>
						<TextField
							label="Respuesta"
							value={rule.reply || ""}
							onChange={event => updateBotRule(index, "reply", event.target.value)}
							fullWidth multiline rows={3} margin="dense" variant="outlined"
						/>
						<Paper className={classes.paper}>
							<Typography variant="body2">Derivar a cola</Typography>
							<Select native value={rule.queueId || ""} className={classes.settingOption}
								onChange={event => updateBotRule(index, "queueId", event.target.value ? Number(event.target.value) : null)}>
								<option value="">No derivar</option>
								{queues.map(queue => <option key={queue.id} value={queue.id}>{queue.name}</option>)}
							</Select>
						</Paper>
						<Paper className={classes.paper}>
							<Typography variant="body2">Al derivar</Typography>
							<Select native value={rule.handoff ? "silent" : "reply"} className={classes.settingOption}
								onChange={event => updateBotRule(index, "handoff", event.target.value === "silent")}>
								<option value="reply">Enviar respuesta configurada</option>
								<option value="silent">No enviar respuesta automática</option>
							</Select>
						</Paper>
						<Paper className={classes.paper}>
							<Typography variant="body2">Regla activa</Typography>
							<Select native value={rule.enabled === false ? "disabled" : "enabled"} className={classes.settingOption}
								onChange={event => updateBotRule(index, "enabled", event.target.value === "enabled")}>
								<option value="enabled">Activa</option><option value="disabled">Pausada</option>
							</Select>
							<IconButton aria-label="Eliminar regla" onClick={() => saveBotRules(botRules.filter((_, itemIndex) => itemIndex !== index))}>
								<DeleteOutline />
							</IconButton>
						</Paper>
					</Paper>
				))}
				<Button variant="outlined" color="primary" startIcon={<Add />}
					onClick={() => setBotRules([...botRules, { enabled: true, keywords: "", matchMode: "contains", reply: "", queueId: null, handoff: false }])}>
					Agregar regla
				</Button>
				<Button color="primary" variant="contained" style={{ marginLeft: 8 }} onClick={() => saveBotRules(botRules)}>
					Guardar reglas
				</Button>

				<Typography className={classes.sectionTitle} variant="body2" gutterBottom>
					Límites de respuestas del bot
				</Typography>
				<Paper className={classes.paper}>
					<Typography variant="body1">Aplicar límite por conversación</Typography>
					{renderEnabledSelect("botReplyLimitEnabled")}
				</Paper>
				{renderTextSetting("botReplyLimitCount", "Máximo de respuestas", { type: "number", inputProps: { min: 1, max: 50, step: 1 } })}
				{renderTextSetting("botReplyLimitWindowMinutes", "Ventana de tiempo (minutos)", { type: "number", inputProps: { min: 1, max: 1440, step: 1 } })}
				{renderTextSetting("botReplyLimitMessage", "Mensaje al alcanzar el límite", { multiline: true, rows: 3 })}
				<Paper className={classes.paper}>
					<Typography variant="body1">Derivar a cola al alcanzar el límite</Typography>
					<Select
						margin="dense"
						variant="outlined"
						native
						name="botReplyLimitQueueId"
						value={getSettingValue("botReplyLimitQueueId")}
						className={classes.settingOption}
						onChange={handleChangeSetting}
					>
						<option value="">No derivar</option>
						{queues.map(queue => <option key={queue.id} value={queue.id}>{queue.name}</option>)}
					</Select>
				</Paper>

				<Typography className={classes.sectionTitle} variant="body2" gutterBottom>
					{i18n.t("settings.dashboard.title")}
				</Typography>

				{renderTextSetting(
					"dashboardSlaMinutes",
					i18n.t("settings.dashboard.slaMinutes"),
					{ type: "number", inputProps: { min: 1, step: 1 } }
				)}

				<Paper className={classes.paper}>
					<TextField
						id="api-token-setting"
						InputProps={{
							readOnly: true,
							type: showToken ? "text" : "password",
							endAdornment: (
								<InputAdornment position="end">
									<IconButton
										aria-label={i18n.t(
											showToken ? "settings.dashboard.hideToken" : "settings.dashboard.showToken"
										)}
										onClick={() => setShowToken(prev => !prev)}
										size="small"
									>
										{showToken ? <VisibilityOff /> : <Visibility />}
									</IconButton>
									<IconButton
										aria-label={i18n.t("settings.dashboard.copyToken")}
										onClick={handleCopyToken}
										size="small"
									>
										<FileCopyOutlined fontSize="small" />
									</IconButton>
								</InputAdornment>
							),
						}}
						label="Token Api"
						margin="dense"
						variant="outlined"
						fullWidth
						value={getSettingValue("userApiToken")}
					/>
				</Paper>

			</Container>
		</div>
	);
};

export default Settings;
